import {
  APIErrorCodes,
  GetRewardsStatusResponse,
  SecretCodeCampaign,
  SecretCodeCampaignParticipant,
  SecretCodeCampaignParticipantStatus,
  SecretCodeCampaignStatus,
  SecretCodeStatus,
  SecretCodeTx,
} from "kz-ui-sdk";
import moment from "moment";

export const SECRET_CODE_LENGTH = 8;

export const normalizeSecretCode = (code: string): string => {
  return code
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .trim()
    .slice(0, SECRET_CODE_LENGTH);
};

/**
 * Type guard to check if secret-code status exists in rewards status response
 */
export function hasSecretCodeStatus(
  response: GetRewardsStatusResponse | undefined,
): response is GetRewardsStatusResponse & { "secret-code": SecretCodeStatus } {
  return response?.["secret-code"] !== undefined;
}

/**
 * Type guard to check if campaign is active
 * Checks both status and date range to ensure campaign is currently active
 */
export function isCampaignActive(campaign: SecretCodeCampaign | null | undefined): boolean {
  if (!campaign) return false;

  // Campaign must be in progress status
  if (campaign.status !== SecretCodeCampaignStatus.InProgress) {
    return false;
  }

  // Check if campaign has expired (endDate is in the past)
  if (campaign.endDate && moment(campaign.endDate).isBefore(moment())) {
    return false;
  }

  return true;
}

/**
 * Check if sharer (participant) should be visible in the UI
 * Sharer is visible if:
 * - Participant is fully redeemed, OR
 * - Participant is issued (in-progress), OR
 * - Participant is expired but within expiredDurationHours grace period
 * Sharer is NOT visible if:
 * - Participant is completed, OR
 * - Participant is expired and past the grace period
 */
export function isSharerVisible(participant: SecretCodeCampaignParticipant | null | undefined): boolean {
  if (!participant) return false;

  // Always show if participant is fully redeemed
  if (participant.status === SecretCodeCampaignParticipantStatus.FullyRedeemed) {
    return true;
  }

  // Never show if participant is completed
  if (participant.status === SecretCodeCampaignParticipantStatus.Completed) {
    return false;
  }

  const campaign = participant.secretCodeCampaign;

  // If participant is expired, check if we're within expiredDurationHours grace period
  if (participant.status === SecretCodeCampaignParticipantStatus.Expired && campaign?.endDate) {
    const endDate = moment(campaign.endDate);
    const expiredDurationHours = campaign.expiredDurationHours || 0;
    const expirationDeadline = moment(endDate).add(expiredDurationHours, "hours");

    // Sharer is no longer visible if we're past the expired duration window
    if (moment().isAfter(expirationDeadline)) {
      return false;
    }
  }

  // For other statuses (e.g., Issued), show the sharer
  return true;
}

/**
 * Type guard to check if member is a sharer (participant exists)
 */
export function isSharer(participant: SecretCodeCampaignParticipant | null | undefined): boolean {
  return participant !== null && participant !== undefined;
}

/**
 * Type guard to check if member has claimed as sharee (secretCodeTx exists)
 */
export function hasClaimedAsSharee(secretCodeTx: SecretCodeTx | null | undefined): boolean {
  return secretCodeTx !== null && secretCodeTx !== undefined;
}

/**
 * Get the number of codes claimed by friends
 */
export function getCodesClaimedCount(participant: SecretCodeCampaignParticipant | null | undefined): number {
  // if participant is fully redeemed, return the total codes generated
  if (participant?.status === SecretCodeCampaignParticipantStatus.FullyRedeemed) {
    return participant?.codesGenerated ?? 0;
  }

  // otherwise, return the current codes claimed
  return participant?.stats?.totalCodeClaimed ?? 0;
}

/**
 * Map SDK error codes to user-friendly translation keys
 * Based on API_ERROR_CODES_MEMBER.md - handles both SDK constants and actual API error code strings
 */
export function mapSecretCodeError(errorCode: string): string {
  switch (errorCode) {
    case APIErrorCodes.SecretCode.CodeRequired:
    case APIErrorCodes.SecretCode.ParticipantNotFound:
    case APIErrorCodes.SecretCode.NotSameBrand:
      return "please enter a valid code";
    case APIErrorCodes.SecretCode.CodeAlreadyFullyRedeemed:
      return "this code has been fully redeemed";
    case APIErrorCodes.SecretCode.SelfClaim:
      return "cannot redeem your own code";
    case APIErrorCodes.SecretCode.CampaignCancelled:
    case APIErrorCodes.SecretCode.CampaignDeleted:
    case APIErrorCodes.SecretCode.CampaignEnded:
    case APIErrorCodes.SecretCode.CampaignExpired:
      return "this code has expired";
    default:
      return errorCode;
  }
}
